import { CheckCircleIcon } from "@/Icons/CheckCircle";
import TrashIcon from "@/Icons/Trash";
import { FileTypeMeta, excelXlsxType } from "@/Utils/fileValidation";
import { joinWithAnd } from "@/Utils/naturalJoin";
import { Button } from "@/components/button";
import { useTrans } from "@/translations";

import { ChangeEvent, useCallback, useContext, useMemo, useRef } from "react";
import { useDropzone } from "react-dropzone";

import { noop } from "lodash";
import { twMerge } from "tailwind-merge";

import { ImportContext } from "../../ImportContext";

export const UploadZone = ({
  allowedFileTypes = [excelXlsxType],
  onAddFile,
  showText = true,
  uploadZoneStyle = "",
  buttonColor = "primary",
  buttonClassName,
  hideButton,
  topTextKey,
  bottomTextKey,
}: {
  allowedFileTypes?: FileTypeMeta[];
  onAddFile: (file: File, clear: () => void) => void;
  showText?: boolean;
  uploadZoneStyle?: string;
  buttonColor?: "primary" | "secondary" | "white" | "black";
  buttonClassName?: string;
  hideButton?: boolean;
  topTextKey?: string;
  bottomTextKey?: string;
}) => {
  const { t } = useTrans();
  const { uploadedFileName, setUploadedFileName, reset } =
    useContext(ImportContext);
  const inputRef = useRef<HTMLInputElement>(null);

  const triggerFileUpload = () => {
    inputRef.current?.click();
  };

  const clear = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [inputRef]);

  const removeDownloadedFile = useCallback(() => {
    setUploadedFileName("");
    reset();
  }, [setUploadedFileName, reset]);

  const { getRootProps } = useDropzone({
    noClick: true,
    maxFiles: 1,
    onDrop: (files) => onAddFile(files[0], clear),
  });

  const onFileAddedWithButton = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        onAddFile(e.target.files[0], clear);
      }
    },
    [onAddFile, clear],
  );

  const allowedExtensions = useMemo(
    () =>
      allowedFileTypes
        .flatMap((type) => type.extensions)
        .map((ext) => `.${ext}`),
    [allowedFileTypes],
  );

  return uploadedFileName ? (
    <div className="bg-grey1 flex h-60 w-full flex-col items-center justify-center">
      <CheckCircleIcon className="mb-2" />
      <div className="text-carrot-green text-sm leading-6 font-medium">
        {t("importLabels:fileUploaded")}
      </div>
      <div className="text-sm leading-6 font-normal">{uploadedFileName}</div>
      <div className="pointer flex items-center" onClick={removeDownloadedFile}>
        <TrashIcon width={12} height={12} />
        <div className="text-sm font-medium underline">
          {t("importLabels:removeFile")}
        </div>
      </div>
    </div>
  ) : (
    <div
      className={twMerge(
        "bg-grey1 flex h-60 w-full flex-col items-center justify-center border border-dashed border-black p-4",
        uploadZoneStyle,
      )}
      {...getRootProps()}
      onClick={hideButton ? triggerFileUpload : noop}
    >
      {showText && (
        <>
          <div className="text-sm leading-6 font-medium">
            {t(topTextKey || "importLabels:dragTitle")}
          </div>
          {(bottomTextKey || allowedFileTypes.length) && (
            <div className="text-sm leading-6 font-normal">
              {t(bottomTextKey || "importLabels:allowedTypes", {
                count: allowedFileTypes.length,
                fileTypes: joinWithAnd(
                  allowedExtensions,
                  t("genericLabels:and"),
                ),
              })}
            </div>
          )}
        </>
      )}
      {!hideButton && (
        <Button
          label="importLabels:uploadFile"
          type="button"
          color={buttonColor}
          onClick={triggerFileUpload}
          className={twMerge("mt-5", buttonClassName)}
        />
      )}
      <input
        type="file"
        ref={inputRef}
        className="hidden"
        onChange={onFileAddedWithButton}
        accept={allowedExtensions.join(", ")}
        multiple={false}
        data-1p-ignore
      />
    </div>
  );
};
